import type { BoardSnapshot } from '@nerds-at-play/contracts';
import { useRef, useState } from 'react';
import { entryApi, entryMessage } from './formation-api.js';
export function GameAdministration({ gameId }: { gameId: string }) {
  const [version, setVersion] = useState<number>();
  const dialog = useRef<HTMLDialogElement>(null);
  const [confirmation, setConfirmation] = useState('');
  const [accessDays, setAccessDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function download() {
    setBusy(true);
    setMessage('');
    try {
      const data = await entryApi('/api/board/export');
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `nerds-at-play-${gameId}-${new Date().toISOString().replaceAll(':', '-')}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setMessage('Export downloaded. Keep it private: it includes every team’s information.');
    } catch (error) {
      setMessage(entryMessage(error));
    } finally {
      setBusy(false);
    }
  }
  async function openReset() {
    setBusy(true);
    setMessage('');
    setConfirmation('');
    try {
      const board = await entryApi<BoardSnapshot>('/api/board');
      if (board.game.gameId !== gameId)
        throw new Error('The game changed. Refresh and sign in again.');
      setVersion(board.game.version);
      dialog.current?.showModal();
    } catch (error) {
      setMessage(entryMessage(error));
    } finally {
      setBusy(false);
    }
  }
  async function reset() {
    setBusy(true);
    setMessage('');
    try {
      await entryApi('/api/board/reset', 'POST', {
        expectedGameVersion: version,
        confirmation,
        accessDays,
      });
      // The GM session belonged to the archived game; sign in again for the new one.
      window.location.assign('/api/auth/gm/login');
    } catch (error) {
      setMessage(entryMessage(error));
      setBusy(false);
    }
  }
  return (
    <details className="game-administration" open>
      <summary>Game management</summary>
      <p>Export a private game record, or start a new game.</p>
      <button disabled={busy} onClick={() => void download()}>
        Export game state
      </button>{' '}
      <button disabled={busy} onClick={() => void openReset()}>
        Reset game…
      </button>
      {!dialog.current?.open && message && <p role="status">{message}</p>}
      <dialog ref={dialog} className="board-dialog live-dialog" aria-labelledby="reset-game-title">
        <h2 id="reset-game-title">Start a fresh game?</h2>
        <p>
          The current game will be archived on the server. A new Foundation starts with an empty
          roster. Teams must request entry again; everyone will be signed out. You will sign in
          again with your Game Master account for the new game.
        </p>
        <p>
          Download an export first if you want your own copy. It includes all teams’ private content
          and history, but excludes login secrets. Automatic import is not available yet.
        </p>
        <button disabled={busy} onClick={() => void download()}>
          Download export before reset
        </button>
        <label>
          Team access lasts (days)
          <input
            type="number"
            min={1}
            max={30}
            step={1}
            value={accessDays}
            onChange={(event) => setAccessDays(Number(event.target.value))}
          />
        </label>
        <label>
          Type RESET to confirm
          <input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
          />
        </label>
        {message && <p role="status">{message}</p>}
        <button disabled={busy} onClick={() => dialog.current?.close()}>
          Cancel
        </button>{' '}
        <button
          disabled={
            busy ||
            version === undefined ||
            confirmation !== 'RESET' ||
            !Number.isInteger(accessDays) ||
            accessDays < 1 ||
            accessDays > 30
          }
          onClick={() => void reset()}
        >
          Archive and reset game
        </button>
      </dialog>
    </details>
  );
}
